import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { 
  Film, Play, Sparkles, BookOpen, Clock, 
  ChevronRight, Star, Monitor, Smartphone
} from 'lucide-react';
import { VideoTutorialCard, VideoPlayerModal } from './VideoTutorial';
import { GifDemoCard, GifDemoGrid } from './GifDemo';
import { WalkthroughCard, InteractiveWalkthrough, WalkthroughCompletionModal } from './InteractiveWalkthrough';
import { 
  getVideoTutorials, 
  getGifDemos, 
  getWalkthroughs 
} from '../../data/multimediaContent';

/**
 * MultimediaSection - Displays all multimedia content for a portal
 * Integrated into DocumentationHub
 */
export function MultimediaSection({ portal, onNavigateToDoc }) {
  const [activeTab, setActiveTab] = useState('videos');
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [activeWalkthrough, setActiveWalkthrough] = useState(null);
  const [completedWalkthroughs, setCompletedWalkthroughs] = useState(() => {
    const stored = localStorage.getItem(`completed-walkthroughs-${portal}`);
    return stored ? JSON.parse(stored) : [];
  });
  const [showCompletion, setShowCompletion] = useState(null);

  const videos = getVideoTutorials(portal);
  const gifs = getGifDemos(portal);
  const walkthroughs = getWalkthroughs(portal);

  // Group GIFs by category
  const gifsByCategory = gifs.reduce((acc, gif) => {
    if (!acc[gif.category]) acc[gif.category] = [];
    acc[gif.category].push(gif);
    return acc;
  }, {});

  const handleVideoPlay = (video) => {
    setSelectedVideo(video);
  };

  const handleWalkthroughStart = (walkthrough) => {
    setActiveWalkthrough(walkthrough);
  };

  const handleWalkthroughComplete = (walkthrough) => {
    setActiveWalkthrough(null);
    setShowCompletion(walkthrough);
    
    // Save completion
    const newCompleted = [...completedWalkthroughs, walkthrough.id];
    setCompletedWalkthroughs(newCompleted);
    localStorage.setItem(`completed-walkthroughs-${portal}`, JSON.stringify(newCompleted));
  };

  const handleWalkthroughDismiss = () => {
    setActiveWalkthrough(null);
  };

  const handleViewDocs = (docId) => {
    setShowCompletion(null);
    onNavigateToDoc?.(docId);
  };

  // Portal-specific styling
  const portalColors = {
    retailer: { bg: 'from-blue-500 to-indigo-600', light: 'bg-blue-50', text: 'text-blue-600' },
    consumer: { bg: 'from-emerald-500 to-teal-600', light: 'bg-emerald-50', text: 'text-emerald-600' },
    admin: { bg: 'from-purple-500 to-pink-600', light: 'bg-purple-50', text: 'text-purple-600' },
    charity: { bg: 'from-rose-500 to-orange-500', light: 'bg-rose-50', text: 'text-rose-600' }
  };
  const colors = portalColors[portal] || portalColors.consumer;

  return (
    <>
      <div className="space-y-6" data-testid={`multimedia-section-${portal}`}>
        {/* Header Card */}
        <Card className={`bg-gradient-to-r ${colors.bg} text-white border-0`}>
          <CardContent className="py-6">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center">
                <Film className="w-7 h-7" />
              </div>
              <div>
                <h2 className="text-xl font-bold">Learning Resources</h2>
                <p className="text-white/80">
                  Video tutorials, quick demos, and interactive guides
                </p>
              </div>
            </div>
            <div className="flex gap-4 mt-4 text-sm">
              <div className="flex items-center gap-2">
                <Film className="w-4 h-4" />
                <span>{videos.length} Videos</span>
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4" />
                <span>{gifs.length} Quick Demos</span>
              </div>
              <div className="flex items-center gap-2">
                <Play className="w-4 h-4" />
                <span>{walkthroughs.length} Guided Tours</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Content Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="videos" className="gap-2">
              <Film className="w-4 h-4" />
              Videos
              <Badge variant="secondary" className="ml-1">{videos.length}</Badge>
            </TabsTrigger>
            <TabsTrigger value="demos" className="gap-2">
              <Sparkles className="w-4 h-4" />
              Quick Demos
              <Badge variant="secondary" className="ml-1">{gifs.length}</Badge>
            </TabsTrigger>
            <TabsTrigger value="walkthroughs" className="gap-2">
              <Play className="w-4 h-4" />
              Guided Tours
              <Badge variant="secondary" className="ml-1">{walkthroughs.length}</Badge>
            </TabsTrigger>
          </TabsList>

          {/* Videos Tab */}
          <TabsContent value="videos" className="mt-0">
            {videos.length > 0 ? (
              <div className="space-y-6">
                {/* Essential Videos */}
                {videos.filter(v => v.priority === 1).length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                      <Star className="w-4 h-4 text-amber-500" />
                      Essential Tutorials
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {videos.filter(v => v.priority === 1).map(video => (
                        <VideoTutorialCard 
                          key={video.id} 
                          video={video} 
                          onPlay={handleVideoPlay}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Other Videos */}
                {videos.filter(v => v.priority !== 1).length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">More Tutorials</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {videos.filter(v => v.priority !== 1).map(video => (
                        <VideoTutorialCard 
                          key={video.id} 
                          video={video} 
                          onPlay={handleVideoPlay}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <EmptyState 
                icon={Film} 
                title="No videos yet" 
                description="Video tutorials are coming soon for this portal."
              />
            )}
          </TabsContent>

          {/* Quick Demos Tab */}
          <TabsContent value="demos" className="mt-0">
            {gifs.length > 0 ? (
              <div className="space-y-6">
                {Object.entries(gifsByCategory).map(([category, categoryGifs]) => (
                  <div key={category}>
                    <h3 className="text-sm font-semibold text-gray-700 mb-3 capitalize">
                      {category} Demos
                    </h3>
                    <GifDemoGrid demos={categoryGifs} columns={2} />
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState 
                icon={Sparkles} 
                title="No demos yet" 
                description="Quick animated demos are coming soon."
              />
            )}
          </TabsContent>

          {/* Walkthroughs Tab */}
          <TabsContent value="walkthroughs" className="mt-0">
            {walkthroughs.length > 0 ? (
              <div className="space-y-4">
                {/* Progress summary */}
                <Card className={colors.light}>
                  <CardContent className="py-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-900">Your Progress</p>
                        <p className="text-sm text-gray-600">
                          {completedWalkthroughs.length} of {walkthroughs.length} tours completed
                        </p>
                      </div>
                      <div className="text-2xl font-bold text-gray-900">
                        {Math.round((completedWalkthroughs.length / walkthroughs.length) * 100)}%
                      </div>
                    </div>
                    <div className="h-2 bg-gray-200 rounded-full mt-3 overflow-hidden">
                      <div 
                        className="h-full bg-emerald-500 transition-all duration-500"
                        style={{ width: `${(completedWalkthroughs.length / walkthroughs.length) * 100}%` }}
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Walkthrough cards */}
                <div className="grid gap-4">
                  {walkthroughs.map(walkthrough => (
                    <WalkthroughCard
                      key={walkthrough.id}
                      walkthrough={walkthrough}
                      onStart={handleWalkthroughStart}
                      completed={completedWalkthroughs.includes(walkthrough.id)}
                    />
                  ))}
                </div>
              </div>
            ) : (
              <EmptyState 
                icon={Play} 
                title="No guided tours yet" 
                description="Interactive walkthroughs are coming soon."
              />
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Video Player Modal */}
      <VideoPlayerModal
        video={selectedVideo}
        isOpen={!!selectedVideo}
        onClose={() => setSelectedVideo(null)}
      />

      {/* Active Walkthrough */}
      <InteractiveWalkthrough
        walkthrough={activeWalkthrough}
        isActive={!!activeWalkthrough}
        onComplete={handleWalkthroughComplete}
        onDismiss={handleWalkthroughDismiss}
      />

      {/* Completion Modal */}
      <WalkthroughCompletionModal
        walkthrough={showCompletion}
        isOpen={!!showCompletion}
        onClose={() => setShowCompletion(null)}
        onViewDocs={handleViewDocs}
      />
    </>
  );
}

/**
 * Empty state component
 */
function EmptyState({ icon: Icon, title, description }) {
  return (
    <Card className="p-8 text-center">
      <Icon className="w-12 h-12 mx-auto text-gray-300 mb-4" />
      <h3 className="font-semibold text-gray-700 mb-2">{title}</h3>
      <p className="text-sm text-gray-500">{description}</p>
    </Card>
  );
}

/**
 * MultimediaPreview - Compact preview for documentation sections
 */
export function MultimediaPreview({ docId, portal }) {
  const videos = getVideoTutorials(portal).filter(v => v.relatedDocs?.includes(docId));
  const walkthroughs = getWalkthroughs(portal).filter(w => w.relatedDocs?.includes(docId));

  if (videos.length === 0 && walkthroughs.length === 0) return null;

  return (
    <div className="mt-4 pt-4 border-t space-y-3">
      <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
        <Film className="w-4 h-4" />
        Related Media
      </h4>
      
      {videos.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {videos.map(video => (
            <Badge 
              key={video.id}
              variant="outline" 
              className="cursor-pointer hover:bg-gray-100"
            >
              <Film className="w-3 h-3 mr-1" />
              {video.title}
              <span className="text-gray-400 ml-1">{video.duration}</span>
            </Badge>
          ))}
        </div>
      )}

      {walkthroughs.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {walkthroughs.map(wt => (
            <Badge 
              key={wt.id}
              variant="outline" 
              className="cursor-pointer hover:bg-emerald-50 border-emerald-200"
            >
              <Play className="w-3 h-3 mr-1 text-emerald-600" />
              {wt.title}
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}

export default MultimediaSection;
